/**
 * Coupon Operations Hook
 * 
 * Provides functions for applying and redeeming coupons
 * Handles attribution data and idempotency
 */

import { useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { getAttributionData, trackCouponApplied } from '../utils/affiliateStorage';
import { v4 as uuidv4 } from 'uuid';
import { authenticatedFetch } from '../utils/authenticatedFetch';

const API_URL = process.env.REACT_APP_API_URL;

interface CouponResult {
  success: boolean;
  message: string;
  data?: any;
  error?: string;
}

interface UseCouponsReturn {
  applyCouponToInvoice: (invoiceId: string, code: string) => Promise<CouponResult>;
  redeemCreditCoupon: (code: string) => Promise<CouponResult>;
  isLoading: boolean;
}

export const useCoupons = (): UseCouponsReturn => {
  const { session } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);

  const applyCouponToInvoice = async (invoiceId: string, code: string): Promise<CouponResult> => {
    if (!session?.user?.id) {
      return { success: false, error: 'User not authenticated', message: 'Please log in to apply coupons' };
    }

    setIsLoading(true);

    try {
      const { affiliate_data } = getAttributionData();
      const idempotencyKey = uuidv4();

      const response = await authenticatedFetch(`${API_URL}/api/coupons/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey
        },
        body: JSON.stringify({
          invoice_id: invoiceId,
          code: code.trim().toUpperCase(),
          user_id: session.user.id,
          affiliate_data
        })
      });

      const result = await response.json();

      if (response.ok) {
        // Track successful application
        trackCouponApplied();
        
        return {
          success: true,
          message: result.message || 'Coupon applied successfully!',
          data: result
        };
      } else {
        return {
          success: false,
          error: result.error,
          message: result.error || 'Failed to apply coupon'
        };
      }
    } catch (error) {
      console.error('Apply coupon error:', error);
      return {
        success: false,
        error: 'Network error',
        message: 'Failed to apply coupon. Please try again.'
      };
    } finally {
      setIsLoading(false);
    }
  };

  const redeemCreditCoupon = async (code: string): Promise<CouponResult> => {
    if (!session?.user?.id) {
      return { success: false, error: 'User not authenticated', message: 'Please log in to redeem coupons' };
    }

    setIsLoading(true);

    try {
      const { affiliate_data } = getAttributionData();
      const idempotencyKey = uuidv4();

      const response = await fetch(`${API_URL}/api/coupons/redeem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey
        },
        body: JSON.stringify({
          code: code.trim().toUpperCase(),
          user_id: session.user.id,
          affiliate_data
        })
      });

      const result = await response.json();

      if (response.ok) {
        return {
          success: true,
          message: result.message || `Successfully redeemed ${result.granted_credits} credits!`,
          data: result
        };
      } else {
        return {
          success: false,
          error: result.error,
          message: result.error || 'Failed to redeem coupon'
        };
      }
    } catch (error) {
      console.error('Redeem coupon error:', error);
      return {
        success: false,
        error: 'Network error',
        message: 'Failed to redeem coupon. Please try again.'
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    applyCouponToInvoice,
    redeemCreditCoupon,
    isLoading
  };
};
